import { Controller } from "@hotwired/stimulus"
import { getSavedTheme } from "../theme"
import {
    LANDSCAPE_KEY,
    TIME_SCENES,
    markSceneryManual,
    needsThemeScenerySync,
    seedSceneryFromServer,
    sceneryStorageKey,
    syncSceneryToSavedTheme
} from "../zen_theme_scenery"
import { persistAppearance } from "../appearance_prefs"
const LANDSCAPES = ["mountain", "future", "koi"]
const LANDSCAPE_LABELS = {
    mountain: "Mountain",
    future: "Future",
    koi: "Koi garden"
}
const SCENERIES = {
    mountain: ["dawn", "day", "dusk", "night", "ocean", "lavender", "forest"],
    future: ["dawn", "day", "dusk", "night", "coast", "garden", "orbit"],
    koi: ["dawn", "day", "dusk", "night", "blossom", "autumn", "moss"]
}
const LABELS = {
    dawn: "Dawn",
    day: "Day",
    dusk: "Dusk",
    night: "Night",
    ocean: "Ocean",
    lavender: "Lavender",
    forest: "Forest",
    coast: "Coastal",
    garden: "Garden city",
    orbit: "Orbital",
    blossom: "Blossom",
    autumn: "Autumn",
    moss: "Moss"
}
const ICONS = {
    dawn: "bi-sunrise",
    day: "bi-sun",
    dusk: "bi-sunset",
    night: "bi-moon-stars",
    ocean: "bi-water",
    lavender: "bi-flower1",
    forest: "bi-tree",
    coast: "bi-tsunami",
    garden: "bi-buildings",
    orbit: "bi-globe2",
    blossom: "bi-flower2",
    autumn: "bi-wind",
    moss: "bi-moisture"
}

export default class extends Controller {
    static targets = ["menuTrigger", "menuLabel", "menuTimeIcon", "menuMoodIcon", "landscapeItem", "sceneryItem"]

    connect() {
        this.hero = this.application.getControllerForElementAndIdentifier(this.element, "assistant-hero")
        this.onScenerySync = this.onScenerySync.bind(this)
        document.addEventListener("emailpa:scenerysync", this.onScenerySync)

        // Restore this member's stored scene before deciding whether the theme
        // should re-tint anything — otherwise a fresh browser re-tints first and
        // the restore lands on an already-overwritten value.
        seedSceneryFromServer()

        if (needsThemeScenerySync(getSavedTheme())) {
            syncSceneryToSavedTheme({ animate: false })
        }

        const savedLandscape = localStorage.getItem(LANDSCAPE_KEY)
        this.landscape = LANDSCAPES.includes(savedLandscape) ? savedLandscape : "mountain"

        this.index = 0
        const saved = localStorage.getItem(this._sceneryKey())
        if (saved) {
            const idx = SCENERIES[this.landscape].indexOf(saved)
            if (idx >= 0) this.index = idx
        } else if (this.hero?._scene) {
            const idx = SCENERIES[this.landscape].indexOf(this.hero._scene)
            if (idx >= 0) this.index = idx
        }

        if (saved && this.hero) this.hero.applyZenScenery(saved, false, this.landscape)

        this.syncUi()
    }

    disconnect() {
        document.removeEventListener("emailpa:scenerysync", this.onScenerySync)
    }

    onScenerySync(event) {
        const { sceneryId, landscape, animate } = event.detail
        if (landscape) this.landscape = landscape

        const idx = SCENERIES[this.landscape].indexOf(sceneryId)
        if (idx >= 0) this.index = idx

        if (this.hero) this.hero.applyZenScenery(sceneryId, animate, this.landscape)
        this.syncUi()
    }

    pickLandscape(event) {
        event.preventDefault()
        const id = event.currentTarget.dataset.landscape
        if (!LANDSCAPES.includes(id)) return

        this.landscape = id
        localStorage.setItem(LANDSCAPE_KEY, this.landscape)

        const saved = localStorage.getItem(this._sceneryKey())
        const idx = saved ? SCENERIES[this.landscape].indexOf(saved) : -1
        this.index = idx >= 0 ? idx : 0

        this._applyAndSave()
    }

    pickScenery(event) {
        event.preventDefault()
        const id = event.currentTarget.dataset.scenery
        const landscape = event.currentTarget.dataset.landscape
        if (landscape !== this.landscape) return

        const idx = SCENERIES[this.landscape].indexOf(id)
        if (idx < 0) return

        this.index = idx
        this._applyAndSave()
    }

    _applyAndSave() {
        const id = SCENERIES[this.landscape][this.index]
        localStorage.setItem(this._sceneryKey(), id)
        markSceneryManual()
        // Only explicit picks are persisted. Theme-derived day/night tints
        // are re-derived on load; custom moods retain their saved look.
        persistAppearance({ zen_landscape: this.landscape, zen_scenery: id })
        if (this.hero) this.hero.applyZenScenery(id, true, this.landscape)
        this.syncUi()
    }

    _sceneryKey() {
        return sceneryStorageKey(this.landscape)
    }

    syncUi() {
        const id = SCENERIES[this.landscape][this.index]
        const landscapeName = LANDSCAPE_LABELS[this.landscape] || "Landscape"
        const sceneryName = LABELS[id] || "Scenery"
        const isTime = TIME_SCENES.includes(id)
        const summary = `${landscapeName}, ${sceneryName}`

        if (this.hasMenuLabelTarget) this.menuLabelTarget.textContent = landscapeName

        if (this.hasMenuTimeIconTarget) {
            this.menuTimeIconTarget.className = `bi zen-scenery-trigger-icon ${ICONS[id] || "bi-sun"}`
            this.menuTimeIconTarget.hidden = !isTime
        }

        if (this.hasMenuMoodIconTarget) {
            this.menuMoodIconTarget.className = `bi zen-scenery-trigger-icon ${ICONS[id] || "bi-image"}`
            this.menuMoodIconTarget.hidden = isTime
        }

        if (this.hasMenuTriggerTarget) {
            this.menuTriggerTarget.title = summary
            this.menuTriggerTarget.setAttribute("aria-label", `Background scene: ${summary}`)
        }

        if (this.hasLandscapeItemTarget) {
            this.landscapeItemTargets.forEach((el) => {
                el.classList.toggle("is-active", el.dataset.landscape === this.landscape)
            })
        }

        if (this.hasSceneryItemTarget) {
            this.sceneryItemTargets.forEach((el) => {
                const matchesLandscape = el.dataset.landscape === this.landscape
                el.hidden = !matchesLandscape
                el.classList.toggle("is-active", matchesLandscape && el.dataset.scenery === id)
            })
        }
    }
}
